// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {useQueries} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {ClipboardEvent, Dispatch, RefObject, SetStateAction} from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {referencesAreEqual} from '../utils/copilot-chat-helpers'
import type {CopilotChatManager} from '../utils/copilot-chat-manager'
import type {CopilotChatState} from '../utils/copilot-chat-reducer'
import type {
  CopilotChatReference,
  DiscussionReference,
  FigmaReference,
  IssueReference,
  PullRequestReference,
  RepositoryReference,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'

const SUPPORTED_LINK_REFERENCE = ['issue', 'pull-request', 'discussion', 'figma']

type ItemRefType = 'issue' | 'pull-request' | 'discussion' | 'figma'

interface BaseUserInputLink {
  originalText: string
  processedText: string
  type: ItemRefType | 'repo'
  loadedRefData?: boolean
}

interface BaseRepoItemReference extends BaseUserInputLink {
  linkNumber?: number
  repository: {
    name: string
    owner: string
  }
}

interface RepositoryInputLink extends BaseRepoItemReference {
  type: 'repo'
}

interface IssueUserInputLink extends BaseRepoItemReference {
  type: 'issue'
}

interface PrUserInputLink extends BaseRepoItemReference {
  type: 'pull-request'
}

interface DiscussionInputLink extends BaseRepoItemReference {
  type: 'discussion'
}

interface FigmaInputLink extends BaseUserInputLink {
  type: 'figma'
  id?: string
}

export type UserInputLink =
  | IssueUserInputLink
  | RepositoryInputLink
  | FigmaInputLink
  | PrUserInputLink
  | DiscussionInputLink

const getTypeParam = (itemRefType?: ItemRefType): string => {
  switch (itemRefType) {
    case 'issue':
      return 'issue'
    case 'pull-request':
      return 'pull_request'
    case 'discussion':
      return 'discussion'
    default:
      // Return issue as default if we can't figure it out
      return 'issue'
  }
}

export function useUserLinks({
  manager,
  setText,
  state,
  text,
  ...props
}: {
  figmaAuthUrl: string | undefined
  manager: CopilotChatManager
  setText: Dispatch<SetStateAction<string>>
  state: CopilotChatState
  text: string
}) {
  const [userInputLinks, setUserInputLinks] = useState<UserInputLink[]>([])
  const filteredUserInputLinks = useMemo(() => userInputLinks.filter(link => !link.loadedRefData), [userInputLinks])
  const combinedQueries = useQueries({
    queries: filteredUserInputLinks.map(link => ({
      enabled: copilotFeatureFlags.copilotUIRefs,
      queryKey: ['copilot', 'chat-links', 'item-url', link.originalText, props.figmaAuthUrl],
      // eslint-disable-next-line @tanstack/query/no-void-query-fn
      queryFn: async () => {
        let response: Response
        if (link.type === 'figma') {
          response = await verifiedFetchJSON(`/copilot/immersive/figma-link?item_url=${link.originalText}`)
        } else if (!copilotFeatureFlags.chatAutocomplete) {
          response = await verifiedFetchJSON(`/copilot/chat-links?item_url=${link.originalText}`)
        } else {
          if (link.type === 'repo') {
            response = await verifiedFetchJSON(
              `/copilot/chat/reference/${(link as BaseRepoItemReference).repository.owner}/${
                (link as BaseRepoItemReference).repository.name
              }`,
            )
          } else {
            response = await verifiedFetchJSON(
              `/copilot/chat/reference/${(link as BaseRepoItemReference).repository.owner}/${
                (link as BaseRepoItemReference).repository.name
              }/${getTypeParam(link.type)}/${(link as BaseRepoItemReference).linkNumber}`,
            )
          }
        }
        if (response.ok) {
          const queryResponse = await response.json()
          if (
            isRepositoryReference(queryResponse) ||
            isIssueReference(queryResponse) ||
            isPullRequestReference(queryResponse) ||
            isDiscussionReference(queryResponse) ||
            isFigmaReference(queryResponse)
          ) {
            return queryResponse
          }
        } else if (link.type === 'figma' && response.status === 403 && props.figmaAuthUrl) {
          // for figma we create this fake reference to give the user a link to initiate oauth with figma
          // would be nice if this could be more general
          const ref: FigmaReference = {
            type: 'figma',
            title: 'Sign in to Figma',
            id: `forbidden-${link.originalText}`,
            url: props.figmaAuthUrl,
            thumbnailUrl: '',
            fullImageUrl: '',
            authenticationRequired: true,
          }
          return ref
        }
      },
    })),
    combine: results => {
      // When a request fails, the `data` is empty and the request is filtered out
      return {
        data: results.map(result => result.data).filter(data => !!data),
        pending: results.some(result => result.isPending),
      }
    },
  })

  // Process completed network requests
  useEffect(() => {
    if (!combinedQueries.pending && combinedQueries.data.length > 0) {
      let newUserInputLinks = userInputLinks
      for (const data of combinedQueries.data) {
        // If the data is not already in the references, add it
        if (
          ['issue', 'pull-request', 'discussion'].includes(data.type) ||
          (copilotFeatureFlags.chatAutocomplete && data.type === 'repository')
        ) {
          newUserInputLinks = newUserInputLinks.map(prevLink => {
            if (referenceAndInputLinkAreEqual(prevLink, data)) {
              return {
                ...prevLink,
                loadedRefData: true,
              }
            }
            return prevLink
          })

          if (!state.currentReferences.find(ref => referencesAreEqual(data, ref))) {
            manager.addReference(data, 'chatInput')
          }
        }

        if (
          state.mode === 'immersive' &&
          copilotFeatureFlags.immersiveFigmaIntegration &&
          data.type === 'figma' &&
          !state.currentReferences.find(ref => referencesAreEqual(data, ref))
        ) {
          newUserInputLinks = newUserInputLinks.map(prevLink => {
            if (referenceAndInputLinkAreEqual(prevLink, data)) {
              return {
                ...prevLink,
                id: data.id,
                loadedRefData: true,
              }
            }
            return prevLink
          })

          if (!state.currentReferences.find(ref => referencesAreEqual(data, ref))) {
            manager.addReference(data, 'chatInput')
          }
        }
      }

      if (copilotFeatureFlags.copilotUIRefs) {
        const {processedText: processedUserInput} = processUserInput(
          text,
          newUserInputLinks,
          state.mode === 'immersive',
          true,
        )

        setText(processedUserInput)
        if (newUserInputLinks !== userInputLinks) {
          setUserInputLinks(newUserInputLinks)
        }
      }
    }
  }, [
    combinedQueries.data,
    combinedQueries.pending,
    manager,
    setText,
    state.currentReferences,
    state.mode,
    text,
    userInputLinks,
  ])

  const prevReferences = useRef(state.currentReferences)
  useEffect(() => {
    if (copilotFeatureFlags.copilotUIRefs && state.currentReferences.length < prevReferences.current.length) {
      // Get the index of any non-matching references that have been removed
      const nonMatchingReferenceIndices = userInputLinks
        .map((userInputLink, index) => {
          const match = state.currentReferences.find(currentRef =>
            referenceAndInputLinkAreEqual(userInputLink, currentRef),
          )
          return match ? -1 : index
        })
        .filter(index => index !== -1)

      // Remove the nonMatchingReferenceIndices from the userInputLinks
      const updatedUserInputLinks = userInputLinks.filter((_, index) => !nonMatchingReferenceIndices.includes(index))
      setUserInputLinks(updatedUserInputLinks)
    }

    prevReferences.current = state.currentReferences
  }, [state.currentReferences, userInputLinks])

  useEffect(() => {
    // process initial text
    const {processedText: processedUserInput, processedRefs} = processUserInput(
      text,
      userInputLinks,
      state.mode === 'immersive',
    )

    setText(processedUserInput)
    setUserInputLinks(processedRefs)
    // we only want to run this once, to find links in the initial text
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const processTextAreaChangeForUserLinks = (userInput: string) => {
    if (copilotFeatureFlags.copilotUIRefs) {
      const {processedText: processedUserInput, processedRefs} = processUserInput(
        userInput,
        userInputLinks,
        state.mode === 'immersive',
        true,
      )

      setText(processedUserInput)
      setUserInputLinks(processedRefs)

      // Remove the reference if the link is removed from the input text
      if (processedRefs.length < prevReferences.current.length) {
        // Find the removed reference(s)
        const removedReference = prevReferences.current.filter(
          ref =>
            SUPPORTED_LINK_REFERENCE.includes(ref.type) &&
            !processedRefs.find(x => referenceAndInputLinkAreEqual(x, ref)),
        )
        // Delete reference from state.currentReferences
        for (const reference of removedReference) {
          manager.removeReference(reference)
        }
      }
    }
  }

  const processPastedTextForUserLinks = (
    event: ClipboardEvent<HTMLTextAreaElement>,
    pastedText: string,
    textAreaRef: RefObject<HTMLTextAreaElement>,
  ) => {
    event.preventDefault()
    const composedText = `${text.substring(0, textAreaRef?.current?.selectionStart ?? 0)}${pastedText}${text.substring(
      textAreaRef?.current?.selectionEnd ?? 0,
      text.length,
    )}`
    const {processedText: processedUserInput, processedRefs} = processUserInput(
      composedText,
      userInputLinks,
      state.mode === 'immersive',
    )

    setText(processedUserInput)
    setUserInputLinks(processedRefs)
  }

  return {
    linkQueries: combinedQueries,
    processPastedTextForUserLinks,
    processTextAreaChangeForUserLinks,
    setUserInputLinks,
    userInputLinks,
  }
}

/**
 * Search the textarea input for supported github-related links
 */
function processUserInput(
  userInput: string,
  previouslyFoundLinks: UserInputLink[],
  isImmersive: boolean,
  requiresTrailingWhiteSpace: boolean = false,
): {processedText: string; processedRefs: UserInputLink[]} {
  const processedRefs: UserInputLink[] = []

  const githubUrlReferencePattern =
    /^(?:http:\/\/github\.localhost|https:\/\/github\.com)\/[^/]+\/[^/]+\/(issues|pull|discussions)\/\d+$/
  const figmaUrlPattern = /^(https:\/\/)?(www\.)?figma\.com\/.+$/

  // Autocomplete Patterns
  const autocompleteRepositoryPattern = /^@([\w-.]+\/[\w-.]+)$/
  const autocompleteItemPattern = /^@([\w-.]+\/[\w-.]+)\/(\w+)\/(\d+)$/

  // Splits the user input and captures any whitespace character (space, tab or newline)
  const splitUserInput = userInput.split(/(\s+)/)
  const processedText = splitUserInput
    .map((text, index) => {
      // Check for duplicates that have already been found
      const duplicateLink = processedRefs.find(ref => ref.originalText === text || ref.processedText === text)
      if (duplicateLink) {
        return duplicateLink.loadedRefData ? duplicateLink.processedText : duplicateLink.originalText
      }

      // Check if link has already been processed
      const prevLink = previouslyFoundLinks.find(link => link.originalText === text || link.processedText === text)
      if (prevLink) {
        processedRefs.push(prevLink)
        // successful network call
        if (prevLink.loadedRefData) return prevLink.processedText
        // network call is still ongoing, still show url in textarea
        return prevLink.originalText
      }

      if (isImmersive && copilotFeatureFlags.immersiveFigmaIntegration && figmaUrlPattern.test(text)) {
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text

        const cleanFigmaUrl = text.replace('https://www.', '')

        processedRefs.push({
          originalText: text,
          processedText: cleanFigmaUrl,
          type: 'figma',
        })

        return text
      }

      if (githubUrlReferencePattern.test(text)) {
        // While the user is typing the url, only process text if there is a space after the url
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text

        // transform link into @${ownerName}/${repoName}/${primitiveType}/${number} format
        const splitIssueUrl = text.split('/')
        const issueNumber = splitIssueUrl[splitIssueUrl.length - 1]!
        const primitiveType = splitIssueUrl[splitIssueUrl.length - 2]!
        const repoName = splitIssueUrl[splitIssueUrl.length - 3]!
        const ownerName = splitIssueUrl[splitIssueUrl.length - 4]!

        processedRefs.push({
          originalText: text,
          processedText: `@${ownerName}/${repoName}/${primitiveType}/${issueNumber}`,
          type: getTypeFromUrlPrimitive(primitiveType),
          linkNumber: Number(issueNumber),
          repository: {
            name: repoName,
            owner: ownerName,
          },
        })
        return text
      }

      // Autocomplete Parsing
      if (autocompleteRepositoryPattern.test(text)) {
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text
        const matches = text.match(autocompleteRepositoryPattern)
        const repo = matches?.[1]

        if (repo) {
          const splitRepo = repo.split('/')
          const ownerName = splitRepo[0]!
          const repoName = splitRepo[1]!
          processedRefs.push({
            originalText: text,
            processedText: text,
            type: 'repo',
            repository: {
              name: repoName,
              owner: ownerName,
            },
          })
        }
        return text
      }
      if (autocompleteItemPattern.test(text)) {
        if (requiresTrailingWhiteSpace && index === splitUserInput.length - 1) return text
        const matches = text.match(autocompleteItemPattern)
        const repo = matches?.[1]
        const type = getTypeFromUrlPrimitive(matches?.[2])
        const issue = matches?.[3]

        if (repo && type && issue) {
          const splitIssueUrl = repo.split('/')
          const ownerName = splitIssueUrl[0]!
          const repoName = splitIssueUrl[1]!

          processedRefs.push({
            originalText: text,
            processedText: text,
            type,
            linkNumber: Number(issue),
            repository: {
              name: repoName,
              owner: ownerName,
            },
          })
        }
        return text
      }

      return text
    })
    .join('')

  return {processedText, processedRefs}
}

/*
 * Convert URL primitive type to our internal type
 */
function getTypeFromUrlPrimitive(primitiveType?: string): UserInputLink['type'] {
  switch (primitiveType) {
    case 'discussions':
      return 'discussion'
    case 'pull':
      return 'pull-request'
    case 'issues':
    default:
      return 'issue'
  }
}

const isIssueReference = (queryResponse: unknown): queryResponse is IssueReference => {
  return (queryResponse as IssueReference).type === 'issue'
}

const isPullRequestReference = (queryResponse: unknown): queryResponse is PullRequestReference => {
  return (queryResponse as PullRequestReference).type === 'pull-request'
}

const isDiscussionReference = (queryResponse: unknown): queryResponse is DiscussionReference => {
  return (queryResponse as DiscussionReference).type === 'discussion'
}

const isFigmaReference = (queryResponse: unknown): queryResponse is FigmaReference => {
  return (queryResponse as FigmaReference).type === 'figma'
}

const isRepositoryReference = (queryResponse: unknown): queryResponse is RepositoryReference => {
  return (queryResponse as RepositoryReference).type === 'repository'
}

const referenceAndInputLinkAreEqual = (inputLink: UserInputLink, reference: CopilotChatReference): boolean => {
  switch (inputLink.type) {
    case 'issue':
    case 'discussion':
    case 'pull-request':
      return inputLink.type === reference.type && inputLink.linkNumber === reference.number
    case 'repo':
      return (
        reference.type === 'repository' &&
        inputLink.repository.owner === reference.ownerLogin &&
        inputLink.repository.name === reference.name
      )
    case 'figma': {
      if (inputLink.type !== reference.type) return false
      const referenceUrl = new URL(reference.url, window.location.origin)
      const referenceKeys = new Set(referenceUrl.searchParams.keys())
      const inputLinkUrl = new URL(inputLink.originalText, window.location.origin)
      for (const k of Array.from(inputLinkUrl.searchParams.keys())) {
        // the response removes some params from the URL so we don't want to match against those
        if (!referenceKeys.has(k)) {
          inputLinkUrl.searchParams.delete(k)
        }
      }
      return areUrlsEqual(inputLinkUrl, referenceUrl)
    }
    default:
      return false
  }
}

/**
 * Compare two URLs to see if they are equal, ignoring query parameters order.
 */
function areUrlsEqual(url1: URL, url2: URL) {
  const normalizeUrl = (url: URL) => {
    // Sort search params
    const sortedSearchParams = [...url.searchParams.entries()]
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
    // Reconstruct URL without altering the original
    return `${url.origin}${url.pathname}?${sortedSearchParams}`
  }

  return normalizeUrl(url1) === normalizeUrl(url2)
}
